"use client";

import { useState, useCallback } from "react";
import Link from "next/link";

const SUGGEST_LINKS = [
  { num: "01", href: "/blog/how-i-structure-go-microservices", title: "How I Structure Go Microservices", kind: "Essay" },
  { num: "02", href: "/blog/from-marquee-tags-to-rag-architectures", title: "From Marquee Tags to RAG Architectures", kind: "Essay" },
  { num: "03", href: "/#portfolio", title: "Selected street & portrait work", kind: "Photos" },
  { num: "04", href: "/#about", title: "About — two crafts, one way of seeing", kind: "Page" },
];

const STACK_TRACE = [
  { ln: "14", fn: "router.match", loc: "(router.ts:128:12)", highlight: false },
  { ln: "15", fn: "handleRequest", loc: "(server.ts:42:9)", highlight: true },
  { ln: "16", fn: "Server.handle", loc: "(node:internal)", highlight: false },
];

function TermLine({
  children,
  dim,
  prompt,
  spaced,
  style,
}: {
  children?: React.ReactNode;
  dim?: boolean;
  prompt?: boolean;
  spaced?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <span className="block" style={{ marginTop: spaced ? "8px" : undefined, ...style }}>
      {prompt && (
        <>
          <span style={{ color: "color-mix(in oklab, var(--accent) 80%, var(--text))" }}>abhinav</span>
          <span className="text-[var(--muted)]">@</span>
          <span style={{ color: "color-mix(in oklab, var(--accent) 80%, var(--text))" }}>bkk</span>
          {" "}<span style={{ color: "#5b9ad6" }}>~/site</span>
          {" "}<span className="text-[var(--accent-text)]">$</span>{" "}
        </>
      )}
      <span className={dim ? "text-[var(--muted)]" : undefined}>{children}</span>
    </span>
  );
}

function TermCode({ children }: { children: React.ReactNode }) {
  return (
    <code style={{
      background: "color-mix(in oklab, var(--accent) 10%, transparent)",
      color: "var(--accent)",
      padding: "0 4px",
      borderRadius: "3px",
    }}>
      {children}
    </code>
  );
}

function readTheme(): "dark" | "light" {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

function readPathname(): string {
  if (typeof window === "undefined") return "/this-page";
  let p = window.location.pathname || "/this-page";
  if (p === "/" || p === "/404.html") p = "/this-page";
  if (p.length > 32) p = p.slice(0, 32) + "…";
  return p;
}

export default function NotFoundClient() {
  const [pathname] = useState(readPathname);
  const [theme, setTheme] = useState(readTheme);

  const toggleTheme = useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    if (next === "light") {
      document.documentElement.setAttribute("data-theme", "light");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    try { localStorage.setItem("theme", next); } catch { /* ignore */ }
  }, [theme]);

  return (
    <>
      <style>{`
        @keyframes nf-glitch1 {
          0%, 92%, 100% { opacity: 0; transform: translate(0,0); }
          93% { opacity: .5; transform: translate(-2px, 1px); color: #5fd47b; }
          96% { opacity: .5; transform: translate(2px, -1px); color: #e57b54; }
        }
        @keyframes nf-glitch2 {
          0%, 88%, 100% { opacity: 0; transform: translate(0,0); }
          90% { opacity: .35; transform: translate(2px, -2px); color: #5b9ad6; }
        }
        @keyframes nf-cursor { to { visibility: hidden; } }

        .nf-o::before, .nf-o::after {
          content: "0";
          position: absolute; inset: 0;
          color: var(--accent); opacity: 0; pointer-events: none;
        }
        .nf-o::before { animation: nf-glitch1 5.2s infinite; }
        .nf-o::after  { animation: nf-glitch2 6.8s infinite; }

        .nf-stage {
          position: relative;
          min-height: calc(100dvh - 64px);
          display: grid;
          grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
          gap: 80px;
          align-items: center;
          max-width: 72rem;
          margin: 0 auto;
          padding: 80px 24px 120px;
        }
        @media (max-width: 880px) {
          .nf-stage { grid-template-columns: 1fr; gap: 56px; padding: 56px 24px 80px; }
        }
        .nf-stage::before {
          content: "";
          position: absolute; inset: 0; z-index: 0;
          pointer-events: none; opacity: 0.04;
          background-image: radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0);
          background-size: 28px 28px;
        }
        [data-theme="dark"]  .nf-stage::before { color: #fff; }
        [data-theme="light"] .nf-stage::before { color: #000; opacity: 0.06; }

        .nf-skyline {
          position: absolute; bottom: 0; left: 0; right: 0;
          height: 180px; z-index: 0; pointer-events: none;
          color: var(--accent); opacity: 0.16;
          -webkit-mask-image: linear-gradient(to top, #000 30%, transparent);
                  mask-image: linear-gradient(to top, #000 30%, transparent);
        }

        .nf-suggest-row { transition: padding 0.18s cubic-bezier(.4,0,.2,1); }
        .nf-suggest-row:hover { padding-left: 8px; }
        .nf-suggest-row:hover .nf-suggest-title { color: var(--accent); }

        .nf-cursor {
          display: inline-block; width: 8px; height: 1em;
          background: var(--accent); vertical-align: text-bottom; margin-left: 2px;
          animation: nf-cursor 1s steps(2,start) infinite;
        }
      `}</style>

      {/* ── Nav ── */}
      <header
        className="sticky top-0 z-50 border-b border-white/5"
        style={{ background: "color-mix(in oklab, var(--bg) 80%, transparent)", backdropFilter: "blur(12px)" }}
      >
        <div className="max-w-[72rem] mx-auto h-16 px-6 flex items-center justify-between">
          <Link href="/" className="font-display text-sm tracking-tight text-[var(--text)] hover:text-[var(--accent-text)] transition-colors">
            <span className="font-light">Abhinav</span>{" "}
            <span className="font-bold">Singh Dangol</span>
          </Link>
          <div className="flex items-center gap-7">
            {[
              { href: "/#about",     label: "About" },
              { href: "/#portfolio", label: "Portfolio" },
              { href: "/blog",       label: "Blog" },
              { href: "/#contact",   label: "Contact" },
            ].map(({ href, label }) => (
              <Link
                key={href} href={href}
                className="hidden md:block font-mono text-xs uppercase tracking-widest text-[var(--muted)] hover:text-[var(--accent-text)] transition-colors"
              >
                {label}
              </Link>
            ))}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="w-8 h-8 flex items-center justify-center text-[var(--muted)] hover:text-[var(--text)] transition-colors"
            >
              {theme === "dark" ? (
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <circle cx="12" cy="12" r="4"/>
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
                </svg>
              ) : (
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                  <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Stage ── */}
      <section className="nf-stage">
        {/* Skyline backdrop */}
        <div className="nf-skyline" aria-hidden="true">
          <svg viewBox="0 0 1440 180" preserveAspectRatio="none" style={{ width: "100%", height: "100%", display: "block" }}>
            <path fill="currentColor" d="M0,180 L0,140 L40,140 L40,110 L80,110 L80,135 L120,135 L120,90 L150,90 L150,75 L180,75 L180,60 L210,60 L210,95 L260,95 L260,120 L310,120 L310,80 L340,80 L340,50 L370,50 L370,85 L420,85 L420,115 L470,115 L470,95 L500,95 L500,70 L540,70 L540,40 L570,40 L570,80 L620,80 L620,110 L670,110 L670,90 L710,90 L710,55 L740,55 L740,30 L780,30 L780,70 L830,70 L830,100 L880,100 L880,75 L920,75 L920,50 L960,50 L960,85 L1010,85 L1010,115 L1060,115 L1060,90 L1100,90 L1100,55 L1140,55 L1140,75 L1190,75 L1190,105 L1240,105 L1240,80 L1280,80 L1280,60 L1320,60 L1320,90 L1370,90 L1370,115 L1410,115 L1410,140 L1440,140 L1440,180 Z"/>
          </svg>
        </div>

        {/* ── Left column ── */}
        <div className="relative z-10">
          <p className="inline-flex items-center gap-3 mb-6 text-[var(--accent-text)] text-[0.75rem] uppercase tracking-[0.3em] font-medium">
            <span className="font-mono text-[0.7rem] font-semibold bg-[var(--accent)] text-[var(--on-accent)] px-2 py-0.5 rounded-[0.25rem]">
              404
            </span>
            Page not found
          </p>

          <h1
            className="font-display flex items-baseline text-[var(--text)] leading-[0.9] tracking-[-0.04em] mb-6 font-light"
            style={{ fontSize: "clamp(4rem, 14vw, 10rem)", gap: "0.05em" }}
          >
            <span className="font-light">4</span>
            <span className="nf-o relative inline-block font-bold text-[var(--accent-text)]">0</span>
            <span className="font-light">4</span>
          </h1>

          <h2
            className="font-display font-light leading-[1.2] tracking-tight mb-5 max-w-[18ch] text-[var(--text)]"
            style={{ fontSize: "clamp(1.5rem, 2.4vw + 0.5rem, 2.5rem)" }}
          >
            This URL is{" "}
            <span className="text-[var(--accent-text)] font-medium">unposed</span>
            {" "}— and not where I framed it.
          </h2>

          <p className="text-base text-[var(--muted)] leading-[1.625] max-w-[48ch] mb-9">
            The route you reached doesn&apos;t exist, has been retired, or was a typo with ambition.
            Try the home page, or browse the writing — I keep most of the good stuff there.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link
              href="/"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-mono text-xs uppercase tracking-widest transition-all bg-[var(--accent)] text-[var(--on-accent)] hover:opacity-90 hover:-translate-y-px"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                <path d="M3 12L12 4l9 8M5 10v10h14V10"/>
              </svg>
              Go home
            </Link>
            <Link
              href="/blog"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-mono text-xs uppercase tracking-widest border border-white/10 text-[var(--text)] transition-all hover:border-[var(--accent)]/50 hover:text-[var(--accent-text)]"
            >
              Read the blog
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                <path d="M5 12h14M13 5l7 7-7 7"/>
              </svg>
            </Link>
            <button
              onClick={() => history.back()}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-mono text-xs uppercase tracking-widest border border-white/10 text-[var(--text)] transition-all hover:border-[var(--accent)]/50 hover:text-[var(--accent-text)]"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back
            </button>
          </div>

          {/* Suggested links */}
          <div className="mt-14">
            <p className="flex items-center gap-3 mb-4 text-[0.65rem] uppercase tracking-[0.3em] text-[var(--muted)]">
              Maybe you were looking for
              <span className="flex-1 h-px bg-white/5" />
            </p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }} className="flex flex-col">
              {SUGGEST_LINKS.map(({ num, href, title, kind }) => (
                <li key={href}>
                  <Link
                    href={href}
                    className="nf-suggest-row flex items-baseline gap-4 py-3.5 border-b border-white/5 last:border-0"
                  >
                    <span className="font-mono text-[0.7rem] text-[var(--muted)] w-7 flex-shrink-0">{num}</span>
                    <span className="nf-suggest-title font-display flex-1 text-[0.95rem] text-[var(--text)] transition-colors duration-[0.18s]">{title}</span>
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-[var(--muted)]">{kind}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Right column: terminal ── */}
        <div className="relative z-10" style={{ perspective: "1200px" }}>
          <div
            className="rounded-lg overflow-hidden border border-white/10"
            style={{
              background: "var(--surface)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.45), 0 4px 16px rgba(0,0,0,0.2)",
              transform: "rotate(-0.5deg)",
            }}
          >
            {/* Traffic-light bar */}
            <div
              className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-white/5"
              style={{ background: "color-mix(in oklab, var(--surface) 60%, var(--bg))" }}
            >
              <div className="flex gap-1.5">
                <span className="w-[11px] h-[11px] rounded-full" style={{ background: "#e57b54" }} />
                <span className="w-[11px] h-[11px] rounded-full" style={{ background: "#d9b65f" }} />
                <span className="w-[11px] h-[11px] rounded-full" style={{ background: "#5fd47b" }} />
              </div>
              <span className="flex-1 text-center font-mono text-[0.7rem] text-[var(--muted)] tracking-[0.08em]">
                ~/abhinavsdangol — zsh — 80×24
              </span>
              <span className="font-mono text-[0.65rem] text-[var(--muted)] opacity-70">404</span>
            </div>

            {/* Shell session */}
            <div
              className="font-mono text-[0.78rem] leading-[1.7] text-[var(--text)]"
              style={{ padding: "18px 18px 22px", minHeight: "320px" }}
            >
              <TermLine prompt>
                curl -I <span className="text-[var(--accent-text)]">{pathname}</span>
              </TermLine>
              <TermLine dim>  → resolving abhinavsdangol.com.np…</TermLine>
              <TermLine dim>  → connected (TLS 1.3, h2)</TermLine>
              <TermLine>
                <span className="text-[var(--muted)]">HTTP/2</span>{" "}
                <span style={{ color: "#e57b54" }}>404</span>{" "}
                <span className="text-[var(--muted)]">Not Found</span>
              </TermLine>
              <TermLine dim>server: cloudflare-pages</TermLine>
              <TermLine dim>x-handler: static</TermLine>
              <TermLine dim>
                x-trace-id:{" "}
                <span className="text-[var(--accent-text)]">2026-05-06T13:42Z · req_8f3a</span>
              </TermLine>

              <TermLine prompt spaced>
                {`grep -r "`}<span className="text-[var(--accent-text)]">{pathname}</span>{`" routes/`}
              </TermLine>
              <TermLine style={{ color: "#e57b54" }}>  no matches found.</TermLine>

              <TermLine prompt spaced>suggest --route</TermLine>
              <TermLine style={{ color: "#5fd47b" }}>
                {"  ✓ Try "}
                <TermCode>/blog</TermCode>{", "}
                <TermCode>/#portfolio</TermCode>{", or "}
                <TermCode>/#about</TermCode>.
              </TermLine>

              <TermLine prompt spaced>
                <span className="nf-cursor" />
              </TermLine>

              {/* Stack trace */}
              <div
                aria-hidden="true"
                className="mt-3 border border-white/5 rounded-[0.25rem] text-[0.72rem] leading-[1.55] text-[var(--muted)]"
                style={{ padding: "10px 12px", background: "var(--bg)" }}
              >
                {STACK_TRACE.map(({ ln, fn, loc, highlight }) => (
                  <div
                    key={ln}
                    className="flex gap-3 py-0.5 overflow-hidden"
                    style={
                      highlight
                        ? { color: "var(--accent)", background: "color-mix(in oklab, var(--accent) 8%, transparent)" }
                        : {}
                    }
                  >
                    <span className="w-6 text-right flex-shrink-0 opacity-50">{ln}</span>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                      at <span style={{ color: "var(--accent)" }}>{fn}</span> {loc}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/5 px-6 py-8">
        <div className="max-w-[72rem] mx-auto flex justify-between items-center gap-4 flex-wrap">
          <p className="text-sm text-[var(--muted)] m-0">
            © {new Date().getFullYear()} Abhinav S. Dangol
          </p>
          <p className="font-mono text-[0.7rem] text-[var(--muted)] tracking-[0.08em] m-0">
            err <span className="text-[var(--accent-text)]">ENOTFOUND</span>
            {" · "}req <span className="text-[var(--accent-text)]">8f3a</span>
            {" · "}
            <Link href="/" className="text-[var(--accent-text)] hover:underline">go home →</Link>
          </p>
        </div>
      </footer>
    </>
  );
}
